import { Bell, Search, X } from "lucide-react";
import { useAuth } from "@/hooks";
import { contactMessageService, artisanService, serviceService, cityService, faqService, testimonialService, teamMemberService } from "@/services/firebase";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import type { Artisan, Service, City, FAQ, Testimonial, TeamMember } from "@/types";

const adminNavigationItems = [
  { title: "Dashboard", category: "Navigation", path: "/admin", icon: "📊", keywords: ["dashboard", "vue", "aperçu"] },
  { title: "Artisans", category: "Gestion", path: "/admin/artisans", icon: "👷", keywords: ["artisans", "professionnels", "prestataires"] },
  { title: "Messages", category: "Communication", path: "/admin/messages", icon: "💬", keywords: ["messages", "contact", "communications"] },
  { title: "Contenu Hero", category: "Contenu", path: "/admin/hero", icon: "🎨", keywords: ["hero", "page d'accueil", "bannière"] },
  { title: "Sections", category: "Contenu", path: "/admin/content", icon: "📝", keywords: ["sections", "contenu", "pages"] },
  { title: "FAQ", category: "Contenu", path: "/admin/faq", icon: "❓", keywords: ["faq", "questions", "aide"] },
  { title: "Témoignages", category: "Contenu", path: "/admin/testimonials", icon: "⭐", keywords: ["témoignages", "avis", "reviews"] },
  { title: "Mission & Vision", category: "Contenu", path: "/admin/mission-vision", icon: "🎯", keywords: ["mission", "vision", "valeurs"] },
  { title: "Administrateurs", category: "Gestion", path: "/admin/admins", icon: "👤", keywords: ["administrateurs", "admins", "utilisateurs"] },
  { title: "Paramètres", category: "Configuration", path: "/admin/settings", icon: "⚙️", keywords: ["paramètres", "configuration", "réglages"] },
  { title: "Equipes", category: "Contenu", path: "/admin/content", icon: "📝", keywords: ["equipe", "monsieur", "biographie"] },
];

export function AdminHeader() {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [dynamicSearchItems, setDynamicSearchItems] = useState<Array<any>>([]);

  // Charger les données dynamiques au montage
  useEffect(() => {
    const loadDynamicContent = async () => {
      try {
        const [artisans, services, cities, faqs, testimonials, teamMembers] = await Promise.all([
          artisanService.getAll(),
          serviceService.getAll(),
          cityService.getAll(),
          faqService.getAll(),
          testimonialService.getAll(),
          teamMemberService.getAll(),
        ]);

        const dynamicItems: Array<any> = [];

        // Artisans
        artisans.forEach((artisan: Artisan) => {
          dynamicItems.push({
            title: artisan.firstName || artisan.lastName,
            category: "Artisan",
            path: `/admin/artisans`,
            icon: "👷",
            keywords: [artisan.firstName?.toLowerCase(), artisan.lastName?.toLowerCase(), artisan.phone],
            searchText: `${artisan.firstName} ${artisan.lastName} ${artisan.city}`,
          });
        });

        // Services
        services.forEach((service: Service) => {
          dynamicItems.push({
            title: service.name,
            category: "Service",
            path: `/admin/content`,
            icon: "🔧",
            keywords: [service.name?.toLowerCase(), "service"],
            searchText: service.name,
          });
        });
        //Equipes
        teamMembers.forEach((member: TeamMember) => {
          dynamicItems.push({
            title: `${member.name} ${member.role}`,
            category: "Équipe",
            path: `/admin/content`,
            icon: "👥",
            keywords: [member.name?.toLowerCase(), member.role?.toLowerCase(), "équipe"],
            searchText: `${member.name} ${member.role}`,
          });
        });
        // Villes
        cities.forEach((city: City) => {
          dynamicItems.push({
            title: city.name,
            category: "Commune",
            path: `/admin/content`,
            icon: "📍",
            keywords: [city.name?.toLowerCase(), "ville", "commune"],
            searchText: city.name,
          });
        });

        // FAQ
        faqs.forEach((faq: FAQ) => {
          dynamicItems.push({
            title: faq.question,
            category: "Question",
            path: `/admin/faq`,
            icon: "❓",
            keywords: [faq.question?.toLowerCase(), "faq", "question"],
            searchText: `${faq.question} ${faq.answer}`,
          });
        });

        // Témoignages
        testimonials.forEach((testimonial: Testimonial) => {
          dynamicItems.push({
            title: testimonial.name,
            category: "Témoignage",
            path: `/admin/testimonials`,
            icon: "⭐",
            keywords: [testimonial.name?.toLowerCase(), "témoignage"],
            searchText: `${testimonial.name} ${testimonial.text}`,
          });
        });

        setDynamicSearchItems(dynamicItems);
      } catch (error) {
        console.error("Erreur chargement contenu dynamique:", error);
      }
    };

    loadDynamicContent();
  }, []);

  const allSearchItems = [...adminNavigationItems, ...dynamicSearchItems];

  const searchResults = searchQuery.trim() === "" ? [] : allSearchItems.filter(item => {
    const query = searchQuery.toLowerCase();
    const searchText = (item.searchText || `${item.title} ${item.category}`).toLowerCase();
    return (
      item.title.toLowerCase().includes(query) ||
      searchText.includes(query) ||
      item.keywords?.some((k: string) => k && k.includes(query))
    );
  }).slice(0, 10); // Limiter à 10 résultats

  useEffect(() => {
    const unsubscribe = contactMessageService.subscribeUnread(setUnreadCount);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const searchContainer = document.querySelector("[data-admin-search]");
      if (searchContainer && !searchContainer.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setSearchOpen(false);
    setSearchQuery("");
  };

  return (
    <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-6 sticky top-0 z-30">
      <div className="flex items-center gap-4">
        <div className="relative hidden sm:block" data-admin-search>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Chercher une section..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              className="pl-9 pr-4 py-2 bg-gray-50 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <AnimatePresence>
            {searchOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full mt-2 left-0 w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
              >
                {searchQuery.trim() !== "" && (
                  <div className="max-h-80 overflow-y-auto">
                    {searchResults.length > 0 ? (
                      <div className="py-2">
                        {searchResults.map((item, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleNavigate(item.path)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-start gap-3"
                          >
                            <span className="text-lg flex-shrink-0">{item.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{item.title}</p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-gray-500">{item.category}</p>
                                {item.path && (
                                  <p className="text-xs text-primary opacity-70">→ {item.path === "/admin" ? "Dashboard" : item.path.split("/").pop()}</p>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        Aucun résultat pour "<strong>{searchQuery}</strong>"
                      </div>
                    )}
                  </div>
                )}

                {searchQuery.trim() === "" && (
                  <div className="p-3 text-xs text-gray-500">
                    <p className="font-medium mb-2 px-1">Accès rapide:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {["Artisans", "Messages", "Médias", "Paramètres"].map(tag => (
                        <button
                          key={tag}
                          onClick={() => setSearchQuery(tag)}
                          className="px-2 py-1.5 bg-gray-100 rounded text-xs hover:bg-gray-200 transition-colors text-left"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-primary font-bold text-sm">
              {(admin?.displayName || "A").charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-dark">{admin?.displayName || admin?.email}</p>
            <p className="text-xs text-gray-400 capitalize">{admin?.role}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
